import { useEffect, useState } from 'react';

/**
 * DeferredMount — mounts its children only after the browser is idle
 * (or after `timeout` ms as a fallback for browsers without
 * requestIdleCallback).
 *
 * Use this to hold back non-critical global widgets (support chat scrapers,
 * ambient guides, floating helpers, session-recorders) until AFTER first
 * paint and Time-to-Interactive, so they don't contribute to Total
 * Blocking Time.
 *
 * Renders nothing (returns null) until the deferral fires; the wrapped
 * children run their own effects at that point as normal.
 */
export default function DeferredMount({ children, timeout = 2000 }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const ric =
      typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : null;

    if (ric) {
      const handle = ric(() => setReady(true), { timeout });
      return () => {
        try {
          (window.cancelIdleCallback || (() => {}))(handle);
        } catch {
          /* ignore */
        }
      };
    }

    // Fallback: setTimeout so it lands after first paint even on Safari
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, [timeout]);

  return ready ? children : null;
}
